/**
 * IndexedDB Database Layer for Dance Music Player PWA
 * Manages persistent storage for:
 * - tracks (with SHA-1 id, file handles, tags, bpm, duration)
 * - tags (ordered list of tags, default flags)
 * - config (directoryHandle, indexingStatus, appState)
 */

import type { Track, Tag, IndexingStatus, AppState } from './types.ts';

const DB_NAME = 'DanceMusicPlayerPWA';
const DB_VERSION = 1;

export const DEFAULT_TAGS: Tag[] = [
    { id: 'tag-club', name: 'Club & Electro', color: '#e91e63', isDefault: true, createdAt: 1, order: 1 },
    { id: 'tag-pulse', name: 'House & Snap', color: '#00bcd4', isDefault: true, createdAt: 2, order: 2 },
    { id: 'tag-slow', name: 'Slow Grooves', color: '#9c27b0', isDefault: true, createdAt: 3, order: 3 },
    { id: 'tag-funk', name: 'Funk & Hip-Hop', color: '#ff9800', isDefault: true, createdAt: 4, order: 4 },
    { id: 'tag-remastered', name: 'Remastered', color: '#1ed760', isDefault: true, createdAt: 5, order: 5 }
];

export const DEFAULT_APP_STATE: AppState = {
    selectedTagId: 'all',
    playbackTagId: 'all',
    currentTrackId: null,
    currentTime: 0,
    isPlaying: false,
    volume: 0.8,
    isMuted: false,
    playbackRate: 1.0,
    repeatMode: 'off',
    isShuffle: false,
    isVisualizerOpen: false,
    activeSort: 'default',
    searchQuery: '',
    viewMode: 'list'
};

export class DatabaseManager {
    private dbPromise: Promise<IDBDatabase> | null = null;

    public getDB(): Promise<IDBDatabase> {
        if (!this.dbPromise) {
            this.dbPromise = new Promise((resolve, reject) => {
                const request = indexedDB.open(DB_NAME, DB_VERSION);

                request.onupgradeneeded = (event) => {
                    const db = (event.target as IDBOpenDBRequest).result;

                    // Tracks Store
                    if (!db.objectStoreNames.contains('tracks')) {
                        const trackStore = db.createObjectStore('tracks', { keyPath: 'id' });
                        trackStore.createIndex('path', 'path', { unique: true });
                        trackStore.createIndex('title', 'title', { unique: false });
                        trackStore.createIndex('favorite', 'favorite', { unique: false });
                    }

                    // Tags Store
                    if (!db.objectStoreNames.contains('tags')) {
                        const tagStore = db.createObjectStore('tags', { keyPath: 'id' });
                        tagStore.createIndex('order', 'order', { unique: false });
                    }

                    // Config & App State Store (key-value)
                    if (!db.objectStoreNames.contains('config')) {
                        db.createObjectStore('config');
                    }
                };

                request.onsuccess = async () => {
                    const db = request.result;
                    // Populate default tags if empty
                    const tags = await this.getAllTagsInternal(db);
                    if (tags.length === 0) {
                        const tx = db.transaction('tags', 'readwrite');
                        const store = tx.objectStore('tags');
                        for (const tag of DEFAULT_TAGS) {
                            store.put(tag);
                        }
                    }
                    resolve(db);
                };

                request.onerror = () => {
                    reject(request.error);
                };
            });
        }
        return this.dbPromise;
    }

    // --- Directory Handle Storage ---
    public async saveDirectoryHandle(handle: FileSystemDirectoryHandle): Promise<void> {
        const db = await this.getDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction('config', 'readwrite');
            const store = tx.objectStore('config');
            const req = store.put(handle, 'directoryHandle');
            req.onsuccess = () => resolve();
            req.onerror = () => reject(req.error);
        });
    }

    public async getDirectoryHandle(): Promise<FileSystemDirectoryHandle | null> {
        const db = await this.getDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction('config', 'readonly');
            const store = tx.objectStore('config');
            const req = store.get('directoryHandle');
            req.onsuccess = () => resolve(req.result || null);
            req.onerror = () => reject(req.error);
        });
    }

    public async removeDirectoryHandle(): Promise<void> {
        const db = await this.getDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction('config', 'readwrite');
            const store = tx.objectStore('config');
            const req = store.delete('directoryHandle');
            req.onsuccess = () => resolve();
            req.onerror = () => reject(req.error);
        });
    }

    // --- App State Storage ---
    public async saveAppState(state: Partial<AppState>): Promise<void> {
        // Fast synchronous mirror in localStorage for instant reliability during unload/refresh
        try {
            const rawLocal = localStorage.getItem('dance_player_state');
            const currentLocal = rawLocal ? JSON.parse(rawLocal) : {};
            delete currentLocal.preservesPitch;
            delete currentLocal.loopA;
            delete currentLocal.loopB;
            delete currentLocal.isABLoopActive;
            const updatedLocal = { ...DEFAULT_APP_STATE, ...currentLocal, ...state };
            localStorage.setItem('dance_player_state', JSON.stringify(updatedLocal));
        } catch (e) {
            // Ignore localStorage quota errors
        }

        const db = await this.getDB();
        const current = await this.getAppState();
        const updated: AppState = { ...current, ...state };
        return new Promise((resolve, reject) => {
            const tx = db.transaction('config', 'readwrite');
            const store = tx.objectStore('config');
            const req = store.put(updated, 'appState');
            req.onsuccess = () => resolve();
            req.onerror = () => reject(req.error);
        });
    }

    public async getAppState(): Promise<AppState> {
        // Check localStorage first for instant, newest state
        try {
            const rawLocal = localStorage.getItem('dance_player_state');
            if (rawLocal) {
                const parsed = JSON.parse(rawLocal);
                return { ...DEFAULT_APP_STATE, ...parsed };
            }
        } catch (e) {
            // Fallback to IndexedDB
        }

        const db = await this.getDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction('config', 'readonly');
            const store = tx.objectStore('config');
            const req = store.get('appState');
            req.onsuccess = () => resolve(req.result ? { ...DEFAULT_APP_STATE, ...req.result } : DEFAULT_APP_STATE);
            req.onerror = () => reject(req.error);
        });
    }

    // --- Indexing Status Storage ---
    public async saveIndexingStatus(status: IndexingStatus): Promise<void> {
        const db = await this.getDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction('config', 'readwrite');
            const store = tx.objectStore('config');
            const req = store.put(status, 'indexingStatus');
            req.onsuccess = () => resolve();
            req.onerror = () => reject(req.error);
        });
    }

    public async getIndexingStatus(): Promise<IndexingStatus | null> {
        const db = await this.getDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction('config', 'readonly');
            const store = tx.objectStore('config');
            const req = store.get('indexingStatus');
            req.onsuccess = () => resolve(req.result || null);
            req.onerror = () => reject(req.error);
        });
    }

    // --- Tracks Store ---
    public async saveTrack(track: Track): Promise<void> {
        const db = await this.getDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction('tracks', 'readwrite');
            const store = tx.objectStore('tracks');
            const req = store.put(track);
            req.onsuccess = () => resolve();
            req.onerror = () => reject(req.error);
        });
    }

    public async saveTracks(tracks: Track[]): Promise<void> {
        const db = await this.getDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction('tracks', 'readwrite');
            const store = tx.objectStore('tracks');
            for (const track of tracks) {
                store.put(track);
            }
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
        });
    }

    public async getTrack(id: string): Promise<Track | null> {
        const db = await this.getDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction('tracks', 'readonly');
            const store = tx.objectStore('tracks');
            const req = store.get(id);
            req.onsuccess = () => resolve(req.result || null);
            req.onerror = () => reject(req.error);
        });
    }

    public async getTrackByPath(path: string): Promise<Track | null> {
        const db = await this.getDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction('tracks', 'readonly');
            const store = tx.objectStore('tracks');
            const index = store.index('path');
            const req = index.get(path);
            req.onsuccess = () => resolve(req.result || null);
            req.onerror = () => reject(req.error);
        });
    }

    public async getAllTracks(): Promise<Track[]> {
        const db = await this.getDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction('tracks', 'readonly');
            const store = tx.objectStore('tracks');
            const req = store.getAll();
            req.onsuccess = () => resolve(req.result || []);
            req.onerror = () => reject(req.error);
        });
    }

    public async deleteTrack(id: string): Promise<void> {
        const db = await this.getDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction('tracks', 'readwrite');
            const store = tx.objectStore('tracks');
            const req = store.delete(id);
            req.onsuccess = () => resolve();
            req.onerror = () => reject(req.error);
        });
    }

    public async clearTracks(): Promise<void> {
        const db = await this.getDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction('tracks', 'readwrite');
            const store = tx.objectStore('tracks');
            const req = store.clear();
            req.onsuccess = () => resolve();
            req.onerror = () => reject(req.error);
        });
    }

    // --- Tags Store ---
    public async getAllTags(): Promise<Tag[]> {
        const db = await this.getDB();
        return this.getAllTagsInternal(db);
    }

    private getAllTagsInternal(db: IDBDatabase): Promise<Tag[]> {
        return new Promise((resolve, reject) => {
            const tx = db.transaction('tags', 'readonly');
            const store = tx.objectStore('tags');
            const index = store.index('order');
            const req = index.getAll();
            req.onsuccess = () => resolve(req.result || []);
            req.onerror = () => reject(req.error);
        });
    }

    public async saveTag(tag: Tag): Promise<void> {
        const db = await this.getDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction('tags', 'readwrite');
            const store = tx.objectStore('tags');
            const req = store.put(tag);
            req.onsuccess = () => resolve();
            req.onerror = () => reject(req.error);
        });
    }

    public async saveTags(tags: Tag[]): Promise<void> {
        const db = await this.getDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction('tags', 'readwrite');
            const store = tx.objectStore('tags');
            for (const tag of tags) {
                store.put(tag);
            }
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
        });
    }

    public async deleteTag(id: string): Promise<void> {
        const db = await this.getDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction('tags', 'readwrite');
            const store = tx.objectStore('tags');
            const req = store.delete(id);
            req.onsuccess = () => resolve();
            req.onerror = () => reject(req.error);
        });
    }
}

export const dbManager = new DatabaseManager();
